import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import CollectionDetailModal from './collections/CollectionDetailModal';
import CollectionView from './collections/CollectionView';
import CollectionWizard from './collections/wizard/CollectionWizard';
import ConfirmDeleteModal from '../components/ConfirmDeleteModal';
import QrCodeBatchGenerator from '../components/QrCodeBatchGenerator';
import useCollectionDetail from './collections/useCollectionDetail';
import useCollectionList from './collections/useCollectionList';
import useCollectionWizard from './collections/useCollectionWizard';
import { btnStyle } from './collections/styles';
import OrganicLoader from '../components/organic/OrganicLoader.jsx';
import useFocusOnOpen from '../utils/useFocusOnOpen';
import { useAuth } from '../context/AuthContext';
import { useSync } from '../context/SyncContext';
import { fieldOutbox } from '../utils/fieldOutbox';



/*
 * The drafts this screen used to keep for itself, and why they are only read
 * here now.
 *
 * Before the sync queue existed, a failed save wrote the whole form to
 * localStorage under this key and an `online` listener re-POSTed it. That was
 * the right instinct and the wrong place: it covered one screen, it could only
 * retry a create, it had no idea whether the server had already accepted a
 * record, and it reported nothing to anybody.
 *
 * utils/offlineQueue now does that job for the whole app. What is left here is
 * a one-way migration: anything still sitting in this key when the screen
 * mounts is moved into the queue and deleted, so an upgrade does not strand
 * work somebody did in a field. Nothing writes to it any more.
 */


// ---------- Collections ----------
export default function Collections() {
  // `map` for the map's own words for what a record is and who recorded it
  // (map:kind.*, map:drawer.observer), so the two screens say them alike.
  const { t } = useTranslation(['collections', 'common', 'regions', 'map', 'voice']);
  const { user } = useAuth();
  const { enqueue } = useSync();
  const canEdit = user?.roles?.some(r => ['collection_manager', 'internal_expert'].includes(r));
  // Students are read-only, even on their own records, unless they also hold the collector role.
  const isCollector = user?.roles?.includes('collector');

  // The table: what the server has, plus what is still in the queue and what
  // Field Mode or the map holds on this device, and the one-off migration of
  // the old per-screen draft store (collections/useCollectionList.js).
  const { rows, queuedIds, loading, error, reload } = useCollectionList();

  // Wizard
  const [tableExpanded, setTableExpanded] = useState(true);
  // Said when work went to the queue instead of to the server. Deliberately
  // not the wizard's own error: nothing went wrong, and colouring it red
  // teaches people to distrust a screen that is behaving correctly. Both of
  // these outlive the wizard, which is why they are declared out here.
  const [queuedNotice, setQueuedNotice] = useState('');
  const [photoWarn, setPhotoWarn]         = useState('');
  // collections/useCollectionWizard.js. The notice setters go in because a
  // queued save and a photograph that did not upload are both reported
  // outside the wizard, and outlive it.
  const wizard = useCollectionWizard({
    t, user, enqueue, reload, setQueuedNotice, setPhotoWarn,
  });

  // collections/useCollectionDetail.js. `enqueue` and the notice setter for
  // the same reason as the wizard: an edit made with no connection goes to
  // this device's queue, and says so outside the modal it closed.
  const detailView = useCollectionDetail({
    t, user, enqueue, reload, setQueuedNotice,
  });


  // Both buttons that open the wizard (the header's and the empty state's)
  // disappear as it opens, so focus moves to the wizard rather than falling
  // to <body>.
  const wizardRef = useFocusOnOpen(wizard.step > 0);

  // Records still on this device (Field Mode, the map) are listed here as
  // "sent in the background": make that true on a page opened cold, as the map
  // does. start() is safe to call again; it only listens and sends.
  useEffect(() => { fieldOutbox().start(); }, []);

  // The table folds away while the wizard is open, and comes back when it
  // closes: on a phone the two together are longer than the screen.
  useEffect(() => {
    setTableExpanded(wizard.step === 0);
  }, [wizard.step === 0]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 20 }}>
        <h2 style={{ margin: 0 }}>{t('pageTitle')}</h2>
        {wizard.step === 0 && (
          <button onClick={wizard.open} data-testid="new-collection" title={t('tip.newCollection')} style={btnStyle('var(--c-primary)')}>{t('newCollectionBtn')}</button>
        )}
        {/* From main: print a sheet of codes BEFORE going to the field,
            where there is no laptop and often no signal. */}
        {wizard.step === 0 && <QrCodeBatchGenerator />}
      </div>

      {photoWarn && (
        <div style={{ background: 'var(--c-warning-soft-2)', border: '1px solid var(--c-warning)', borderRadius: 8, padding: '12px 16px', marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, fontSize: 14, color: 'var(--c-warning-text-fg)' }}>
          <span>⚠ {photoWarn}</span>
          <button onClick={() => setPhotoWarn('')} title={t('tip.dismissNotice')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--c-warning-text-fg)', fontSize: 18, padding: 0, flexShrink: 0, lineHeight: 1 }}>×</button>
        </div>
      )}

      {wizard.step > 0 && (
        <div ref={wizardRef} tabIndex={-1} role="region" aria-label={t('wizard.title')} data-testid="collection-wizard">
        <CollectionWizard
          t={t} user={user} canEdit={canEdit}
          step={wizard.step} setStep={wizard.setStep} onClose={wizard.close}
          canAdvance={wizard.canAdvance}
          form={wizard.form} onFormChange={wizard.onFormChange} setForm={wizard.setForm}
          vegetation={wizard.vegetation}
          regionField={wizard.regionField} vegField={wizard.vegField}
          taxonField={wizard.taxonField}
          photoField={wizard.photoField} suppField={wizard.suppField}
          draftSaved={wizard.draftSaved} error={wizard.error} setError={wizard.setError}
          saving={wizard.saving} onSubmit={wizard.submit} />
        </div>
      )}

      {/* Saved here, not sent. Green rather than red: the work is safe, and
          the global SyncStatus banner owns the "when will it go" half. */}
      {queuedNotice && (
        <div data-testid="queued-notice"
          style={{ background: 'var(--c-primary-soft)', border: '1px solid var(--c-primary-border-2)', borderRadius: 8, padding: '12px 16px', marginBottom: 20, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, fontSize: 14, color: 'var(--c-success-2-fg)' }}>
          <span>{queuedNotice}</span>
          <button onClick={() => setQueuedNotice('')} title={t('tip.dismissNotice')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--c-success-2-fg)', fontSize: 18, padding: 0, flexShrink: 0, lineHeight: 1 }}>×</button>
        </div>
      )}

      {loading && (
        <OrganicLoader block motif="soil" label={t('common:loading')} slowLabel={t('common:stillWorking')} />
      )}
      {error && <p role="alert" style={{ color: 'var(--c-danger-fg)' }}>{error}</p>}
      {wizard.resumeError && <p role="alert" style={{ color: 'var(--c-danger-fg)' }}>{wizard.resumeError}</p>}

      <CollectionView
        t={t} rows={rows} queuedIds={queuedIds} loading={loading} error={error}
        expanded={tableExpanded} setExpanded={setTableExpanded}
        onOpen={detailView.open} onResumeDraft={wizard.resume}
        onCreate={wizard.step === 0 ? wizard.open : null}
        user={user} canEdit={canEdit} isCollector={isCollector} />

      <CollectionDetailModal
        t={t} user={user} canEdit={canEdit} isCollector={isCollector}
        detailView={detailView} onResumeDraft={wizard.resume} />

      <ConfirmDeleteModal
        open={!!detailView.deleteVegTarget}
        title={t('detail.vegetationDeleteConfirm')}
        deleting={detailView.deletingVeg}
        onCancel={() => detailView.setDeleteVegTarget(null)}
        onConfirm={detailView.confirmDeleteVeg}
      />
    </div>
  );
}

